import {RefCountableBase} from "@cm/utils"
import type {Three as THREE} from "@cm/material-nodes/three"

const TEXTURE_CACHE_KEY = "__cmTextureCacheKey"

class CachedTexture extends RefCountableBase {
    constructor(
        readonly texture: THREE.Texture,
        readonly downloadUrl: string,
        disposeFn: () => void,
    ) {
        super({disposeFn})
    }
}

export class ThreeTextureCache {
    private readonly entries = new Map<string, CachedTexture>()
    private readonly pending = new Map<string, Promise<THREE.Texture>>()

    async acquire(key: string, downloadUrl: string, load: () => Promise<THREE.Texture>): Promise<THREE.Texture> {
        const existing = this.entries.get(key)
        if (existing) {
            existing.addRef()
            return existing.texture
        }

        let pending = this.pending.get(key)
        if (!pending) {
            pending = load()
            this.pending.set(key, pending)
        }

        let texture: THREE.Texture
        try {
            texture = await pending
        } finally {
            this.pending.delete(key)
        }

        const entry = this.entries.get(key)
        if (entry) {
            entry.addRef()
            return entry.texture
        }

        texture.userData[TEXTURE_CACHE_KEY] = key
        this.entries.set(
            key,
            new CachedTexture(texture, downloadUrl, () => {
                this.entries.delete(key)
                texture.dispose()
            }),
        )
        return texture
    }

    release(texture: THREE.Texture): boolean {
        const key = texture.userData[TEXTURE_CACHE_KEY]
        if (typeof key !== "string") return false

        const entry = this.entries.get(key)
        if (!entry) return true

        entry.release()
        return true
    }

    hasForDownloadUrl(downloadUrl: string): boolean {
        for (const entry of this.entries.values()) {
            if (entry.downloadUrl === downloadUrl) return true
        }
        return false
    }

    clear(): void {
        for (const entry of this.entries.values()) entry.texture.dispose()
        this.entries.clear()
        this.pending.clear()
    }
}
